import * as React from 'react';
import styled, { css } from 'styled-components';

import { Card, Text } from '~/styles';

interface DetailsCardProps {
  columns?: number;
  details: {
    label: string;
    value: React.ReactNode;
  }[];
}

const DetailWrapper = styled.div`
  background: ${({ theme }) => theme.body};
  break-inside: avoid;
  padding: 12px 16px;
  box-sizing: border-box;
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:first-child {
    border-top: none;
  }
`;

const StyledCard = styled(Card)`
  background: ${({ theme }) => theme.body};
  overflow: hidden;
  container-type: inline-size;
`;

const DetailsGrid = styled.div<{ $columns?: number }>`
  display: grid;
  gap: 0 1px;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
  grid-auto-flow: row;
  grid-template-rows: auto;
  grid-template-columns: 1fr;

  @container (min-width: 400px) {
    ${({ $columns }) =>
      $columns
        ? css`
            grid-auto-flow: row;
            grid-template-columns: repeat(${$columns}, minmax(0, 1fr));
            grid-template-rows: auto;
          `
        : css`
            grid-auto-flow: column;
            grid-template-rows: repeat(2, 1fr);
            grid-auto-columns: 1fr;
          `}

    ${DetailWrapper} {
      ${({ $columns }) => ($columns ? `&:nth-child(-n + ${$columns})` : '&:nth-child(odd)')} {
        border-top: none;
      }
    }
  }
`;

const DetailValue = styled.div`
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  font-size: 14px;
  font-weight: 500;
`;

const Detail: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <DetailWrapper>
    <Text size={12} weight="normal">
      {label}
    </Text>
    {typeof value === 'string' ? (
      <Text color="foreground" size={14} weight="medium">
        {value}
      </Text>
    ) : (
      <DetailValue>{value}</DetailValue>
    )}
  </DetailWrapper>
);

export const DetailsCard: React.FC<DetailsCardProps> = ({ columns, details }) => (
  <StyledCard variant="secondary">
    <DetailsGrid $columns={columns}>
      {details.map((detail) => (
        <Detail key={detail.label} {...detail} />
      ))}
    </DetailsGrid>
  </StyledCard>
);
